import * as React from 'react';
import {
    Avatar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { TestAccount, UserDetails } from '../../api/users.interface';
import { buildTestAccountLabel } from './testAccounts.utils';

interface Props {
    visible: boolean;
    submitting?: boolean;
    onCancel: () => void;
    onSearch: (value: string) => Promise<UserDetails | undefined>;
    onConfirm: (testAccount: TestAccount) => Promise<boolean>;
}

const AddTestAccountDialog: React.FC<Props> = ({
    visible,
    submitting,
    onCancel,
    onSearch,
    onConfirm,
}) => {
    const [searchValue, setSearchValue] = React.useState('');
    const [candidate, setCandidate] = React.useState<UserDetails>();
    const [confirming, setConfirming] = React.useState(false);
    const [searching, setSearching] = React.useState(false);

    React.useEffect(() => {
        if (!visible) {
            setSearchValue('');
            setCandidate(undefined);
            setConfirming(false);
            setSearching(false);
        }
    }, [visible]);

    const handleSearch = async () => {
        setSearching(true);
        const user = await onSearch(searchValue);
        setCandidate(user);
        setConfirming(Boolean(user));
        setSearching(false);
    };

    const handleConfirm = async () => {
        if (!candidate) {
            return;
        }

        const success = await onConfirm({
            uid: candidate.uid,
            avatar: candidate.avatar,
            username: candidate.username,
            name: candidate.name,
            lastname: candidate.lastname,
            email: candidate.email,
            addedAt: Date.now(),
            hasOriginalProfileBackup: false,
            currentClonedProfile: undefined,
            restoredAt: undefined,
        });

        if (success) {
            onCancel();
        }
    };

    const candidateLabel = candidate
        ? buildTestAccountLabel({
            uid: candidate.uid,
            avatar: candidate.avatar,
            username: candidate.username,
            name: candidate.name,
            lastname: candidate.lastname,
            email: candidate.email,
            addedAt: 0,
        })
        : '';

    return (
        <>
            <Dialog
                fullWidth
                maxWidth="sm"
                open={visible && !confirming}
                onClose={onCancel}
            >
                <DialogTitle>Add Test Account</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            Enter the email address or UID for the account you want to
                            designate as a test account.
                        </Typography>
                        <TextField
                            label="Email or UID"
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                            fullWidth
                            size="small"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onCancel} variant="outlined">Cancel</Button>
                    <Button
                        onClick={handleSearch}
                        variant="contained"
                        disabled={!searchValue.trim() || searching}
                    >
                        {searching ? 'Searching...' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                fullWidth
                maxWidth="sm"
                open={visible && confirming}
                onClose={onCancel}
            >
                <DialogTitle>Confirm Test Account</DialogTitle>
                <DialogContent dividers>
                    {candidate && (
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar src={candidate.avatar} alt={candidate.username} />
                                <Box>
                                    <Typography variant="h6">{candidateLabel}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {candidate.email}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                UID: {candidate.uid}
                            </Typography>
                            <Typography variant="body2">
                                This account will be added to the test account registry and
                                shown in the clone target dropdown.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setConfirming(false)} variant="outlined">
                        Back
                    </Button>
                    <Button onClick={handleConfirm} variant="contained" disabled={submitting}>
                        {submitting ? 'Saving...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AddTestAccountDialog;
